/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { a11y } from './a11y.js';
import { browser, getVendorCSS } from './browser.js';
import { isDarkColor, parseColor } from './color.js';
import { emit } from './events.js';
import {
    injectParams,
    isExternalLink,
    rewriteInternalUrlsForApp,
} from './links.js';
import { rewriteImageURL, rewriteMediaURL } from './media.js';
import { limit, numStr } from './util.js';

/*
 * The map itself: a d3-driven graph of positioned elements.
 *
 * `d3` is a global, and stays one. This is a customised fork of D3 3.5.6
 * with no npm equivalent, served as a plain script from
 * `public/vendor/d3.js`, so the module reads it off the window rather than
 * pretending it can import it.
 */

const d3 = window.d3 || {};

export const KontextMap = {};

/**
 * Selects the icon size variant for a given maximum rendered pixel dimension.
 * @param {number} maxRenderedPx - Maximum rendered dimension (node size × zoom scale).
 * @returns {'xsmall'|'small'|'default'|'large'}
 */
KontextMap.iconVariantForRenderedSize = (maxRenderedPx) => {
    if (maxRenderedPx > 1280) return 'large';
    if (maxRenderedPx > 640) return 'default';
    if (maxRenderedPx > 320) return 'small';
    return 'xsmall';
};

/**
 * Returns the format suffix for an icon URL based on its file extension.
 *
 * For lossy formats (jpg / jpeg / webp) the best format the browser supports
 * is used (`_detectedFormatSuffix`). PNG sources always keep their format.
 * GIF, SVG, and unrecognized extensions return `null`, indicating that no
 * format or variant rewriting should be applied.
 *
 * @param {string} originalUrl
 * @returns {string|null} A suffix like `'.avif'`, `'.png'`, or `null`.
 */
KontextMap.iconFormatSuffix = (originalUrl) => {
    const ext = originalUrl.split('.').pop().toLowerCase();
    switch (ext) {
        case 'jpg':
        case 'jpeg':
        case 'webp':
            return KontextMap._detectedFormatSuffix;
        case 'png':
            return '.png';
        default:
            return null;
    }
};

/**
 * Detects the best supported lossy image format for background-image icons.
 * Result is stored in `KontextMap._detectedFormatSuffix` ('.avif', '.webp',
 * or '.jpg') and callbacks registered via `_formatDetectionCallbacks` are
 * invoked when detection completes.
 */
KontextMap._detectedFormatSuffix = '.jpg';
KontextMap._formatDetectionDone = false;
KontextMap._formatDetectionCallbacks = [];
(() => {
    function resolve(suffix) {
        KontextMap._detectedFormatSuffix = suffix;
        KontextMap._formatDetectionDone = true;
        KontextMap._formatDetectionCallbacks.forEach((cb) => cb());
        KontextMap._formatDetectionCallbacks = [];
    }
    const avifImg = new Image();
    avifImg.onload = () => resolve('.avif');
    avifImg.onerror = () => {
        const webpImg = new Image();
        webpImg.onload = () => resolve('.webp');
        webpImg.onerror = () => resolve('.jpg');
        webpImg.src =
            'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAUAmJYgCdAEO/gHOAAA=';
    };
    // Minimal 1×1 AVIF image for capability detection
    avifImg.src =
        'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABcAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQAMAAAAABNjb2xybmNseAACAAIABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAAB9tZGF0EgAKBzgADlAQ0GkykCIgAAAiQAAAAbA=';
})();

/**
 * Returns `true` if the given URL points to a client-side animation file
 * that requires a dedicated player library (e.g. lottie-web) instead of
 * a CSS background-image.
 *
 * Currently recognized extensions:
 *   - `.lottie` (dotLottie container)
 *   - `.lottie.json` (plain-JSON Lottie format)
 *
 * @param {string} url
 * @returns {boolean}
 */
KontextMap.isAnimationUrl = (url) => {
    if (!url) return false;
    const lower = url.split('?')[0].toLowerCase();
    return (
        lower.endsWith('.lot') ||
        lower.endsWith('.lottie') ||
        lower.endsWith('.lottie.json') ||
        lower.endsWith('.json')
    );
};

/**
 * Returns `true` if the URL points to a binary dotLottie container
 * (`.lottie`) rather than a plain-JSON Lottie file (`.lot`, `.lottie.json`, `.json`).
 *
 * @param {string} url
 * @returns {boolean}
 */
KontextMap.isDotLottieUrl = (url) => {
    if (!url) return false;
    const lower = url.split('?')[0].toLowerCase();
    return lower.endsWith('.lottie');
};

/**
 * Returns the CSS position class string for a node's ``title_position``
 * design value (new-schema snake_case values).
 *
 * :param position: The ``title_position`` value from node design data.
 * :returns: A two-word CSS class string such as ``'top center'`` or
 *     ``'middle left'``.  Defaults to ``'middle center'`` for unknown values.
 * :rtype: string
 */
KontextMap.titlePositionClass = (position) => {
    const titlePositions = {
        'top-left': 'top left',
        top: 'top center',
        'top-right': 'top right',
        left: 'middle left',
        center: 'middle center',
        right: 'middle right',
        'bottom-left': 'bottom left',
        bottom: 'bottom center',
        'bottom-right': 'bottom right'
    };
    return titlePositions[position] || 'middle center';
};

/** @typedef {import('./types.d.js').Edge} Edge */
/** @typedef {import('./types.d.js').Node} Node */
/** @typedef {import('./types.d.js').MapElement} MapElement */
/** @typedef {import('./types.d.js').MapElements} MapElements */
/** @typedef {import('./types.d.js').MapManager} MapManager */

/**
 * Returns an object with the center coordinates and the size of the given node.
 * The calculation takes into account border options.
 * @param {Node} node
 * @return {{center: {x: number, y: number}, size: {width: number, height: number, width_2: number, height_2: number}}}
 */
KontextMap.getNodeDimensions = (node) => {
    const width = node.design.width;
    const height = node.design.height;
    const borderEnabled = node.design.border_enabled !== false;
    const borderWidth = (borderEnabled ? node.design.border_width : 0.0) || 0.0;
    const totalWidth = width + 2.0 * borderWidth;
    const totalHeight = height + 2.0 * borderWidth;
    return {
        center: node.geometry.center,
        size: {
            width: totalWidth,
            height: totalHeight,
            width_2: totalWidth / 2.0,
            height_2: totalHeight / 2.0
        }
    };
};

/**
 * This function calculates the intersection points between a line segment
 * defined by the object `lineSegment` with the following properties:
 *
 *     start: the coordinates of the starting point of the line segment
 *     end: the coordinates of the end point of the line segment
 *
 * and a rotated ellipse defined by the object `ellipse`, which should have
 * the following properties:
 *
 *     center: the coordinates of the center of the ellipse
 *     majorAxis: the length of the horizontal axis (width) of the ellipse
 *     minorAxis: the length of the vertical axis (height) of the ellipse
 *     rotation: the rotation angle of the ellipse in radians
 *
 * The function returns an array of intersection points, where each point
 * is represented as an object with properties x and y. If there are no
 * intersection points, the function returns an empty array.
 *
 * @param {{start: {x: number, y: number}, end: {x: number, y: number}}} lineSegment Object describing the line segment
 * @param {{center: {x: number, y: number}, majorAxis: Number, minorAxis: Number, rotation: Number}} ellipse Object describing the rotated ellipse
 * @returns {[{x: number, y: number}]|[]}
 */
KontextMap.intersectLineSegmentAndEllipse = (lineSegment, ellipse) => {
    // Transform the line and ellipse to a coordinate system where
    // the ellipse is not rotated
    const theta = ellipse.rotation || 0.0;
    const cosTheta = Math.cos(theta);
    const sinTheta = Math.sin(theta);
    const rotatedLine = {
        start: {
            x: lineSegment.start.x * cosTheta - lineSegment.start.y * sinTheta,
            y: lineSegment.start.x * sinTheta + lineSegment.start.y * cosTheta
        },
        end: {
            x: lineSegment.end.x * cosTheta - lineSegment.end.y * sinTheta,
            y: lineSegment.end.x * sinTheta + lineSegment.end.y * cosTheta
        }
    };
    const rotatedEllipse = {
        center: {
            x: ellipse.center.x * cosTheta - ellipse.center.y * sinTheta,
            y: ellipse.center.x * sinTheta + ellipse.center.y * cosTheta
        },
        majorAxis: ellipse.majorAxis,
        minorAxis: ellipse.minorAxis,
        rotation: ellipse.rotation
    };

    // Calculate the coefficients of the quadratic equation
    const a =
        rotatedEllipse.majorAxis *
            rotatedEllipse.majorAxis *
            (rotatedLine.start.y - rotatedLine.end.y) ** 2 +
        rotatedEllipse.minorAxis *
            rotatedEllipse.minorAxis *
            (rotatedLine.start.x - rotatedLine.end.x) ** 2;
    const b =
        2 *
        (rotatedEllipse.majorAxis *
            rotatedEllipse.majorAxis *
            (rotatedLine.start.y - rotatedLine.end.y) *
            (rotatedEllipse.center.y - rotatedLine.start.y) +
            rotatedEllipse.minorAxis *
                rotatedEllipse.minorAxis *
                (rotatedLine.start.x - rotatedLine.end.x) *
                (rotatedEllipse.center.x - rotatedLine.start.x));
    const c =
        rotatedEllipse.majorAxis *
            rotatedEllipse.majorAxis *
            (rotatedEllipse.center.y - rotatedLine.start.y) ** 2 +
        rotatedEllipse.minorAxis *
            rotatedEllipse.minorAxis *
            (rotatedEllipse.center.x - rotatedLine.start.x) ** 2 -
        rotatedEllipse.majorAxis *
            rotatedEllipse.majorAxis *
            (rotatedEllipse.minorAxis * rotatedEllipse.minorAxis);

    // Solve the quadratic equation
    const discriminant = b * b - 4 * a * c;

    if (discriminant < 0) {
        // No intersection
        return [];
    } else if (discriminant === 0) {
        // One intersection
        const t = -b / (2 * a);
        if (t >= 0 && t <= 1) {
            // The solution lies within the line segment
            const x =
                rotatedLine.start.x +
                t * (rotatedLine.end.x - rotatedLine.start.x);
            const y =
                rotatedLine.start.y +
                t * (rotatedLine.end.y - rotatedLine.start.y);
            return [{ x, y }];
        } else {
            // The intersection lies outside the line segment
            return [];
        }
    } else {
        // Two intersections
        const sqrtDiscriminant = Math.sqrt(discriminant);
        const t1 = (-b + sqrtDiscriminant) / (2 * a);
        const t2 = (-b - sqrtDiscriminant) / (2 * a);
        const intersections = [];
        if (t1 >= 0 && t1 <= 1) {
            // The first solution lies within the line segment
            const x =
                rotatedLine.start.x +
                t1 * (rotatedLine.end.x - rotatedLine.start.x);
            const y =
                rotatedLine.start.y +
                t1 * (rotatedLine.end.y - rotatedLine.start.y);
            intersections.push({ x, y });
        }
        if (t2 >= 0 && t2 <= 1) {
            // The second solution lies within the line segment
            const x =
                rotatedLine.start.x +
                t2 * (rotatedLine.end.x - rotatedLine.start.x);
            const y =
                rotatedLine.start.y +
                t2 * (rotatedLine.end.y - rotatedLine.start.y);
            intersections.push({ x, y });
        }
        return intersections;
    }
};

/**
 * The function returns an intersection point of two line segments, which
 * both have the following properties:
 *
 *     start: the coordinates of the starting point of the line segment
 *     end: the coordinates of the end point of the line segment
 *
 * If there are no intersection, the function returns `null`.
 *
 * @param {{start: {x: number, y: number}, end: {x: number, y: number}}} segment1
 * @param {{start: {x: number, y: number}, end: {x: number, y: number}}} segment2
 * @returns {{x: number, y: number}|null}
 */
KontextMap.intersectLineSegments = (segment1, segment2) => {
    const { start: p1, end: p2 } = segment1;
    const { start: q1, end: q2 } = segment2;

    // Calculate the differences
    const s1x = p2.x - p1.x;
    const s1y = p2.y - p1.y;
    const s2x = q2.x - q1.x;
    const s2y = q2.y - q1.y;

    // Determinant
    const denom = -s2x * s1y + s1x * s2y;

    // If denom is 0, lines are parallel or collinear
    if (denom === 0) {
        return null;
    }

    // Calculate the intersection point
    const s = (-s1y * (p1.x - q1.x) + s1x * (p1.y - q1.y)) / denom;
    const t = (s2x * (p1.y - q1.y) - s2y * (p1.x - q1.x)) / denom;

    // If s and t are within [0, 1], the segments intersect
    if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
        return {
            x: p1.x + t * s1x,
            y: p1.y + t * s1y
        };
    }

    // No intersection
    return null;
};

/**
 * Utility function that converts an array of coordinate objects to an
 * array of line segments.
 *
 * @param {[{x: number, y: number}]} polygonPoints
 * @returns {[{start: {x: number, y: number}, end: {x: number, y: number}}]}
 */
KontextMap.polygonPointsToSegments = (polygonPoints) => {
    const segments = [];
    for (let i = 0; i < polygonPoints.length; i++) {
        segments.push({
            start: polygonPoints[i],
            end: polygonPoints[(i + 1) % polygonPoints.length]
        });
    }
    return segments;
};

/**
 * This function calculates the intersection points between a line segment
 * defined by the object `lineSegment` with the following properties:
 *
 *     start: the coordinates of the starting point of the line segment
 *     end: the coordinates of the end point of the line segment
 *
 * and a (closed) polygon line defined by a list of coordinate objects.
 *
 * The function returns an array of intersection points, where each point
 * is represented as an object with properties x and y. If there are no
 * intersection points, the function returns an empty array.
 *
 * @param {{start: {x: number, y: number}, end: {x: number, y: number}}} lineSegment Object describing the line segment
 * @param {[{x: number, y: number}]} polygonPoints Array with coordinates describing the polygon
 * @returns {[{x: number, y: number}]|[]}
 */
KontextMap.intersectLineSegmentAndPolygon = (
    lineSegment,
    polygonPoints
) => {
    const polygonSegments =
        KontextMap.polygonPointsToSegments(polygonPoints);
    const intersectionPoints = [];
    polygonSegments.forEach((polygonSegment) => {
        const intersectionPoint = KontextMap.intersectLineSegments(
            lineSegment,
            polygonSegment
        );
        if (intersectionPoint !== null) {
            intersectionPoints.push(intersectionPoint);
        }
    });
    return intersectionPoints;
};

/**
 * Chooses the point from an array of coordinates that has the highest
 * distance from the specified origin point.
 *
 * @param {{x: number, y: number}} origin
 * @param {[{x: number, y: number}]} points
 * @returns {{x: number, y: number}|null}
 */
KontextMap.farthestPoint = (origin, points) => {
    if (points.length === 0) {
        return null;
    } else if (points.length === 1) {
        return points[0];
    }
    let maxDistanceSquared;
    let maxDistancePoint;
    points.forEach((point) => {
        const dx = point.x - origin.x;
        const dy = point.y - origin.y;
        const distanceSquared = dx * dx + dy * dy;
        if (
            maxDistanceSquared === undefined ||
            distanceSquared > maxDistanceSquared
        ) {
            maxDistanceSquared = distanceSquared;
            maxDistancePoint = point;
        }
    });
    return maxDistancePoint;
};

/**
 * Returns the intersection point between a line segment of an edge and a
 * node object.
 *
 * @param {{start: {x: number, y: number}, end: {x: number, y: number}}} edgeSegment
 * @param {Node} node
 * @returns {{x: number, y: number}|null}
 */
KontextMap.intersectEdgeNode = (edgeSegment, node) => {
    if (!node.design.width || !node.design.height) {
        return { ...node.geometry.center };
    }
    const dims = KontextMap.getNodeDimensions(node);
    const rotation = (node.design.rotate / 180.0) * Math.PI || 0.0;

    let geometry;
    let intersect;
    switch (node.design.shape) {
        case 'rectangle': {
            const cosTheta = Math.cos(rotation);
            const sinTheta = Math.sin(rotation);
            geometry = [
                {
                    x:
                        dims.center.x -
                        (dims.size.width_2 * cosTheta +
                            dims.size.height_2 * sinTheta),
                    y:
                        dims.center.y -
                        (dims.size.width_2 * sinTheta +
                            dims.size.height_2 * cosTheta)
                },
                {
                    x:
                        dims.center.x +
                        (dims.size.width_2 * cosTheta +
                            dims.size.height_2 * sinTheta),
                    y:
                        dims.center.y -
                        (dims.size.width_2 * sinTheta +
                            dims.size.height_2 * cosTheta)
                },
                {
                    x:
                        dims.center.x +
                        (dims.size.width_2 * cosTheta +
                            dims.size.height_2 * sinTheta),
                    y:
                        dims.center.y +
                        (dims.size.width_2 * sinTheta +
                            dims.size.height_2 * cosTheta)
                },
                {
                    x:
                        dims.center.x -
                        (dims.size.width_2 * cosTheta +
                            dims.size.height_2 * sinTheta),
                    y:
                        dims.center.y +
                        (dims.size.width_2 * sinTheta +
                            dims.size.height_2 * cosTheta)
                }
            ];
            intersect = KontextMap.intersectLineSegmentAndPolygon;
            break;
        }
        default:
            // Assume it’s an ellipse
            geometry = {
                center: dims.center,
                majorAxis: dims.size.width_2,
                minorAxis: dims.size.height_2,
                rotation: rotation
            };
            intersect = KontextMap.intersectLineSegmentAndEllipse;
    }

    const intersectionPoints = intersect(edgeSegment, geometry);
    return KontextMap.farthestPoint(dims.center, intersectionPoints);
};

/**
 *
 * @param {String} viewportSelector
 * @param {MapElements} mapData
 * @param {{additionalParams: Object, fallbackLanguage: String, language: String, maxScale: number, modelHeight: number, modelWidth: number, stepsPerLevel: number, touchZoomFactor: number, zoomLevels: number}} options
 * @return {MapManager}
 */
KontextMap.init = (viewportSelector, mapData, options) => {
    if (!options) {
        options = {};
    }

    const minZoomLevel = 1;
    const maxZoomLevel = options.zoomLevels || 12;
    const maxZoomLevelScale = options.maxScale || 3;
    const zoomStepsPerLevel = options.stepsPerLevel || 3;
    const touchZoomFactor = options.touchZoomFactor || 1;
    const language = options.language || 'und';
    const fallbackLanguage = options.fallbackLanguage || 'und';
    const additionalParams = options.additionalParams || {};

    function translateStr(value, langCode, defaultLangCode) {
        if (value !== null && typeof value === 'object') {
            return value[langCode] || '';
        } else {
            return value || '';
        }
    }

    /**
     * Removes hidden elements from an array.
     * @param {Array<MapElement>} elements
     * @returns {Array<MapElement>}
     */
    function filterVisible(elements) {
        return (elements || []).filter((el) => !el.visibility?.hidden);
    }

    /** @type {Array<Edge>} */
    const filteredEdges = filterVisible(mapData.edges);
    /** @type {Array<Node>} */
    const filteredNodes = filterVisible(mapData.nodes);

    /** @type {Map<string, Node>} */
    const nodesById = new Map();
    (mapData.nodes || []).forEach((n) => nodesById.set(String(n.id), n));

    const viewport = d3.select(viewportSelector).classed('zoom-level-1', true);
    const modelWidth = options.modelWidth || 1920;
    const modelHeight = options.modelHeight || 1200;
    const model = viewport.select('.map-model').style({
        left: '0',
        top: '0',
        width: `${modelWidth}px`,
        height: `${modelHeight}px`
    });
    const modelBg = model
        .append('div')
        .attr('class', 'map-bg')
        .style({
            left: '0',
            top: '0',
            width: `${modelWidth}px`,
            height: `${modelHeight}px`
        });

    let viewportWidth = Number.parseInt(viewport.style('width'), 10);
    let viewportHeight = Number.parseInt(viewport.style('height'), 10);

    function addCssRule(sheet, selector, rules, index) {
        if ('insertRule' in sheet) {
            sheet.insertRule(`${selector}{${rules}}`, index);
        } else if ('addRule' in sheet) {
            sheet.addRule(selector, rules, index);
        }
    }

    function styleSheet(media) {
        // Create the <style> tag
        const style = document.createElement('style');
        style.setAttribute('type', 'text/css');
        if (media) {
            style.setAttribute('media', media);
        }
        // WebKit hack :(
        style.appendChild(document.createTextNode(''));
        // Add the <style> element to the page
        document.head.appendChild(style);
        return style.sheet;
    }

    function createVisibilityStyles() {
        const sheet = styleSheet();
        const rangeIndex = {};
        let key;
        [filteredEdges, filteredNodes].forEach((data) => {
            data.forEach((d) => {
                if (!d.visibility) return;
                key = `${d.visibility.min_level || minZoomLevel}-${
                    d.visibility.max_level || maxZoomLevel
                }`;
                rangeIndex[key] = true;
                if (
                    translateStr(d.content.start, language, fallbackLanguage) ||
                    translateStr(
                        d.content.middle,
                        language,
                        fallbackLanguage
                    ) ||
                    translateStr(d.content.end, language, fallbackLanguage)
                ) {
                    const minLevel = d.visibility.min_level || minZoomLevel;
                    const maxLevel = d.visibility.max_level || maxZoomLevel;
                    if (minLevel < maxLevel) {
                        key = `${minLevel + 1}-${maxLevel}`;
                        rangeIndex[key] = true;
                    }
                }
            });
        });
        const allRanges = Object.keys(rangeIndex);

        // Make all elements invisible by default
        const rangesSelector = allRanges
            .map((rangeKey) => `.map2 .visible-${rangeKey}`)
            .join(', ');
        if (!rangesSelector) {
            return;
        }
        // `visibility` is what actually removes an element from the tab
        // order and the accessibility tree -- `opacity: 0` leaves it
        // focusable and announced. The delay is asymmetric on purpose: the
        // hidden rule waits out the 1s opacity fade before flipping, while
        // the visible rule below flips immediately, because a transition
        // uses the timing of the state it is moving *to*.
        const transitionProp = getVendorCSS('transition');
        const hiddenRule = `
            ${transitionProp}: opacity 1s, visibility 0s linear 1s;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
        `;
        const visibleRule = `
            ${transitionProp}: opacity 1s, visibility 0s linear 0s;
            pointer-events: auto;
            opacity: 1;
            visibility: visible;
        `;
        addCssRule(sheet, rangesSelector, hiddenRule, 0);

        // Show elements in their range
        const visibilitiesSelectors = allRanges
            .map((rangeKey) => {
                const range = rangeKey.split('-');
                const minLevel = +range[0];
                const maxLevel = +range[1];
                const levels = [];
                for (let l = minLevel; l <= maxLevel; l++) {
                    levels.push(l);
                }
                return levels
                    .map((l) => `.zoom-level-${l} .visible-${rangeKey}`)
                    .join(', ');
            })
            .filter((val) => val !== null && val.trim() !== '');
        if (visibilitiesSelectors.length) {
            const individualSelectors = visibilitiesSelectors
                .reduce((acc, curr) => acc.concat(curr.split(', ')), [])
                .map((selector) => `${selector}.visited:not(.active)`);
            addCssRule(
                sheet,
                visibilitiesSelectors.join(', ').replace(/[, ]+$/, ''),
                visibleRule,
                1
            );
            addCssRule(
                sheet,
                individualSelectors.join(', ').replace(/[, ]+$/, ''),
                `${getVendorCSS(
                    'transition'
                )}: opacity 1s; opacity: 0.5;`,
                1
            );
        }
    }

    createVisibilityStyles();

    const x = d3.scale.linear().domain([0, viewportWidth]);
    const y = d3.scale.linear().domain([0, viewportHeight]);

    const modelScale = 1.0;
    let scaleOld = 1.0;

    function updateModelTransform(alreadyAnimated) {
        const scale = zoom.scale();
        const translate = zoom.translate();
        const tx = translate[0];
        const ty = translate[1];
        const transform = [
            `translate(${numStr(tx)}px, ${numStr(ty)}px)`,
            `scale(${numStr(scale / modelScale)})`
        ].join(' ');
        if (!alreadyAnimated) {
            // This is disabled due to stutter-y movements when the user zooms (in FF and Safari).
            //model.style(getVendorCSS('transition'), 'transform 0.3s cubic-bezier(0.2, 0.5, 0.6, 1)');
        }
        model.style(getVendorCSS('transform'), transform);
        const inverseTransform = [
            `scale(${numStr(modelScale / scale)})`,
            `translate(${numStr(-tx)}px, ${numStr(-ty)}px)`
        ].join(' ');
        model
            .select('.tx-exclude')
            .style(getVendorCSS('transform'), inverseTransform);
    }

    //let zoomRefinementTimerId;
    function zoomed(animated) {
        //clearTimeout(zoomRefinementTimerId);

        // Current pan and zoom
        const scale = zoom.scale();
        const scaleChanged = scale !== scaleOld;
        const translate = zoom.translate();
        const tx = translate[0];
        const ty = translate[1];

        // Notify D3.js's zoom behavior of the corrected values
        zoom.translate([tx, ty]);

        model.style(
            getVendorCSS('transition'),
            animated
                ? `${getVendorCSS('transform')} 0.5s linear`
                : 'none'
        );
        updateModelTransform(animated);

        // Semantic zooming
        // (see <http://bl.ocks.org/mbostock/3680957>)
        if (scaleChanged) {
            const zoomLevel =
                Number.parseInt(
                    Math.round(
                        Math.log(zoom.scale()) / Math.log(zoom.zoomFactor()) -
                            minLevel
                    ) / zoomStepsPerLevel,
                    10
                ) + 1;
            const curLevel = limit(
                zoomLevel,
                minZoomLevel,
                maxZoomLevel
            );

            for (let l = minZoomLevel; l <= maxZoomLevel; l++) {
                viewport.classed(`zoom-level-${l}`, l === curLevel);
            }

            // Update icon size variants based on the new scale.
            // Animation icons are resolution-independent and don't need
            // variant swapping on zoom.
            const newScale = zoom.scale();
            model.selectAll('.node').each(function (d) {
                const originalUrl = d.design?._iconOriginalUrl;
                if (!originalUrl) return;
                if (KontextMap.isAnimationUrl(originalUrl)) return;
                const customIcon = this.querySelector('.custom-icon');
                if (!customIcon) return;
                const dims = KontextMap.getNodeDimensions(d);
                const maxRendered =
                    Math.max(dims.size.width, dims.size.height) * newScale;
                const newVariant =
                    KontextMap.iconVariantForRenderedSize(maxRendered);
                if (customIcon.dataset.iconVariant === newVariant) return;
                customIcon.dataset.iconVariant = newVariant;
                customIcon.style.backgroundImage = `url(${computeIconUrl(d, originalUrl, newScale)})`;
            });
        }

        const eventInfo = {
            scale: scale,
            scaleOld: scaleOld,
            translate: [tx, ty]
        };
        // Emits the event both channels of the bus carry, so a
        // DOM-native one and the internal bus, so new subscribers do not
        emit(viewportSelector, 'map:zoom', [eventInfo]);

        scaleOld = scale;
    }

    function getNodeShapeClass(d) {
        const shape = d.design?.shape;
        if (!shape) {
            return '';
        }
        return `node-shape-${shape}`;
    }

    function getZoomRangeClass(d) {
        if (!d.visibility) {
            return '';
        }
        const minLevel = limit(
            d.visibility.min_level || minZoomLevel,
            minZoomLevel,
            maxZoomLevel
        );
        const maxLevel = limit(
            d.visibility.max_level || maxZoomLevel,
            minZoomLevel,
            maxZoomLevel
        );
        return `visible-${minLevel}-${maxLevel}`;
    }

    function initPinchZoom(selection, map) {
        if (!browser.isMac) {
            return;
        }

        let initialScale = 1;

        function gestureStart() {
            initialScale = map.scale();
            d3.event.preventDefault();
        }

        function gestured() {
            const e = d3.event;
            const center = [e.layerX, e.layerY];
            const newScale = initialScale * e.scale;
            map.zoomTo(newScale, center);
            e.preventDefault();
        }

        selection
            .on('gesturechange', gestured)
            .on('gesturestart', gestureStart)
            .on('gestureend', gestured);
    }

    const zoom = d3.behavior
        .zoom()
        .x(x)
        .y(y)
        .scaleExtent([minZoomLevel, Number.POSITIVE_INFINITY])
        .zoomFactor(
            Math.exp(
                Math.log(maxZoomLevelScale) /
                    (maxZoomLevel * zoomStepsPerLevel - minZoomLevel + 1)
            )
        )
        .touchZoomFactor(touchZoomFactor)
        .on('zoom', zoomed);

    const mapObj = {
        width() {
            return modelWidth;
        },
        height() {
            return modelHeight;
        },
        viewport() {
            const translate = zoom.translate();
            const scale = zoom.scale();
            return {
                x: -translate[0] / scale,
                y: -translate[1] / scale,
                width: viewportWidth / scale,
                height: viewportHeight / scale
            };
        },
        center() {
            const translate = zoom.translate();
            const scale = zoom.scale();
            return [
                (-translate[0] + viewportWidth / 2) / scale,
                (-translate[1] + viewportHeight / 2) / scale
            ];
        },
        scale() {
            return zoom.scale();
        },
        scaleForZoomLevel(zoomLevel) {
            return (
                zoom.zoomFactor() **
                ((zoomLevel - minZoomLevel) * zoomStepsPerLevel + minLevel)
            );
        },
        zoomFactor() {
            return zoom.zoomFactor();
        },
        zoomLevel() {
            const zoomLevel =
                Number.parseInt(
                    Math.round(
                        Math.log(zoom.scale()) / Math.log(zoom.zoomFactor()) -
                            minLevel
                    ) / zoomStepsPerLevel,
                    10
                ) + 1;
            return limit(zoomLevel, minZoomLevel, maxZoomLevel);
        },
        centerTo(centerX, centerY, zoomLevel, animated) {
            const curTranslate = zoom.translate();
            const curScale = zoom.scale();
            const scale =
                zoomLevel !== undefined
                    ? this.scaleForZoomLevel(zoomLevel)
                    : curScale;
            const tx =
                centerX !== undefined
                    ? -centerX * scale + viewportWidth / 2
                    : curTranslate[0];
            const ty =
                centerY !== undefined
                    ? -centerY * scale + viewportHeight / 2
                    : curTranslate[1];
            zoom.scale(scale);
            zoom.translate([tx, ty]);
            zoomed(animated);
        },
        zoomTo(newScale, center, animated) {
            const minScale = zoom.zoomFactor() ** minLevel;
            const curTranslate = zoom.translate();
            const curScale = zoom.scale();
            const tx = curTranslate[0];
            const ty = curTranslate[1];
            const cx = center[0];
            const cy = center[1];

            // Restrict zoom out, but allow infinite zoom in.
            if (newScale < minScale) {
                newScale = minScale;
            }
            // Delta between viewport and cursor
            const vpToCursorDeltaX = tx - cx;
            const vpToCursorDeltaY = ty - cy;
            // Change in viewport position
            const vpPosDeltaX =
                (vpToCursorDeltaX * newScale) / curScale - vpToCursorDeltaX;
            const vpPosDeltaY =
                (vpToCursorDeltaY * newScale) / curScale - vpToCursorDeltaY;
            // New viewport values
            const ntx = tx + vpPosDeltaX;
            const nty = ty + vpPosDeltaY;
            zoom.scale(newScale);
            zoom.translate([ntx, nty]);
            zoom.event(viewport);
            zoomed(animated);
        },
        nodeActive(nodeElem, activate) {
            const node = d3.select(nodeElem);
            const isActive = node.classed('active');
            // Usage as getter
            if (activate === undefined) {
                return isActive;
            }
            // Only one node can be selected -> deactivate all other nodes first
            nodes.classed('active', false);
            if (activate) {
                node.classed('active', true);
            }
        },
        highlight(nodeIds) {
            const mapModel = document.querySelector('.map-model');
            const backdrop = mapModel.querySelector('.highlight-backdrop');
            const mapElems = mapModel.querySelectorAll('.edge, .node');
            if (!nodeIds || !nodeIds.size) {
                backdrop.classList.remove('highlight-backdrop--active');
                mapElems.forEach((mapElem) =>
                    mapElem.classList.remove('highlighted')
                );
            } else {
                mapElems.forEach((mapElem) => {
                    const elemData = mapElem.__data__ || {};
                    let isConnected = false;
                    if (mapElem.matches('.edge')) {
                        isConnected =
                            nodeIds.has(String(elemData.node.from_id)) &&
                            nodeIds.has(String(elemData.node.to_id));
                    } else if (mapElem.matches('.node')) {
                        isConnected = nodeIds.has(String(elemData.id));
                    }
                    mapElem.classList.toggle('highlighted', isConnected);
                });
                backdrop.classList.add('highlight-backdrop--active');
            }
        }
    };

    viewport.call(zoom).call(initPinchZoom, mapObj);
    // Disable built-in double click zoom of d3 behavior
    viewport.on('dblclick.zoom', null);

    const mapDesign = mapData.map.design || {};
    const bgColor = mapDesign.bg_color || '#FFFFFF';
    if (/-gradient/.test(bgColor)) {
        viewport.style('background-image', bgColor);
    } else {
        viewport.style('background-color', bgColor);
    }

    const bgMedia = mapDesign.media || mapDesign.media_url;
    if (bgMedia) {
        const bgMediaUrl = isExternalLink(bgMedia)
            ? bgMedia
            : rewriteImageURL(bgMedia, {
                  useLegacy: true,
                  variant: 'large'
              });
        const bgMediaPosition = mapDesign.media_position;
        const bgMediaRepeat = mapDesign.media_repeat;
        const bgMediaSize = {
            contain: 'contain',
            cover: 'cover',
            fill: '100% 100%',
            none: 'auto'
        }[mapDesign.media_size];
        const bgMediaOpacity = (mapDesign.media_opacity || 100) / 100.0;
        modelBg.style({
            'background-image': `url(${bgMediaUrl})`,
            'background-position': bgMediaPosition,
            'background-repeat': bgMediaRepeat,
            'background-size': bgMediaSize,
            opacity: bgMediaOpacity
        });
    }

    /*
     * Apply resolved font families from design as CSS custom properties.
     * Fonts are stored as font_url values in the design sub-objects and
     * resolved to CSS font-family strings via design._font_url_to_family.
     * The server-side template sets these vars for DB-backed fonts; this
     * client-side pass covers fonts stored only in the map file's design.
     */
    {
        const urlToFamily = mapData.design?._font_url_to_family ?? {};
        const fontVarSources = [
            [
                mapData.design?.popups?.small?.font_url,
                '--popup-panel-font-family'
            ],
            [
                mapData.design?.popups?.marginal?.font_url,
                '--popup-articles-font-family'
            ],
            [
                mapData.design?.popups?.marginal?.font_url,
                '--popup-links-font-family'
            ],
            [
                mapData.design?.tooltips?.font_url,
                '--popup-tooltips-font-family'
            ],
            [
                mapData.design?.table_of_contents?.font_url,
                '--toc-all-font-family'
            ]
        ];
        fontVarSources.forEach(([fontUrl, cssVar]) => {
            const family = fontUrl ? urlToFamily[fontUrl] ?? fontUrl : null;
            if (family)
                document.documentElement.style.setProperty(cssVar, family);
        });
    }

    function findFirst(key, objects) {
        const values = objects.map((obj) => obj[key]);
        return values.find((val) => val != null);
    }

    /**
     * Apply design inheritance to an edge.
     * @param {Edge} edge
     */
    function setEdgeDesignData(edge) {
        const useObjectTemplate =
            edge.design.object_template != null &&
            edge.design.object_template !== '0';
        const objectTemplateExists = Object.hasOwn(
            this.templates ?? {},
            edge.design.object_template
        );
        const objectTemplate =
            (useObjectTemplate && objectTemplateExists
                ? this.templates[edge.design.object_template]
                : null) || {};
        const useGlobalDesign = edge.design.object_template === '0';
        const globalDesign =
            (useGlobalDesign && this.edges ? this.edges : null) || {};

        const lookupObjs = [
            edge.design || {},
            objectTemplate,
            globalDesign,
            this.defaults.edges
        ];
        Object.keys(this.defaults.edges).forEach(
            (key) => (edge.design[key] = findFirst(key, lookupObjs))
        );
        edge.design._fontFamily =
            this._fontUrlToFamily?.[edge.design.font_url] ??
            edge.design.font_url;
    }

    /**
     * Apply design inheritance to a node.
     * @param {Node} node
     */
    function setNodeDesignData(node) {
        const useObjectTemplate =
            node.design.object_template != null &&
            node.design.object_template !== '0';
        const objectTemplateExists = Object.hasOwn(
            this.templates ?? {},
            node.design.object_template
        );
        const objectTemplate =
            (useObjectTemplate && objectTemplateExists
                ? this.templates[node.design.object_template]
                : null) || {};
        const useGlobalDesign = node.design.object_template === '0';
        const globalDesign =
            (useGlobalDesign && this.nodes ? this.nodes : null) || {};

        const lookupObjs = [
            node.design || {},
            objectTemplate,
            globalDesign,
            this.defaults.nodes
        ];
        Object.keys(this.defaults.nodes).forEach(
            (key) => (node.design[key] = findFirst(key, lookupObjs))
        );

        // Resolve media icon URL with template/global-design inheritance
        const nodeMedia =
            findFirst('media', [node.design, objectTemplate, globalDesign]) ||
            findFirst('media_url', [node.design, objectTemplate, globalDesign]);
        if (nodeMedia) {
            if (isExternalLink(nodeMedia))
                node.design._iconUrl = nodeMedia;
            else {
                node.design._iconOriginalUrl = nodeMedia;
                node.design._iconUrl = rewriteImageURL(nodeMedia, {
                    useLegacy: true,
                    variant: KontextMap.iconVariantForRenderedSize(
                        Math.max(node.design.width, node.design.height)
                    )
                });
            }
        }
        node.design._fontFamily =
            this._fontUrlToFamily?.[node.design.font_url] ??
            node.design.font_url;
    }

    function applyTemplateValues(nodes, edges, design) {
        design = design || {};
        nodes.forEach(setNodeDesignData, design);
        edges.forEach(setEdgeDesignData, design);
    }

    if (mapData.design == null) mapData.design = {};
    mapData.design._fontUrlToFamily = mapData.design._font_url_to_family ?? {};
    applyTemplateValues(filteredNodes, filteredEdges, mapData.design);

    /**
     * Appends `additionalParams` to a URL (e.g. an access-code token).
     * A thin wrapper so callers don't need to reference `additionalParams`
     * directly and the intent is self-documenting.
     * @param {string} url
     * @returns {string}
     */
    function applyAccessParams(url) {
        return injectParams(url, additionalParams);
    }

    /**
     * Returns the final URL for a node icon at the given zoom scale.
     *
     * Steps applied in order:
     *  1. External links – inject access params and return as-is.
     *  2. GIF / SVG / unknown formats – rewrite the domain/path prefix for
     *     CNAME client domains (`rewriteInternalUrlsForApp`), then inject
     *     access params.  Variant and format are left unchanged.
     *  3. PNG / JPG / WebP – select the size variant via
     *     `iconVariantForRenderedSize`, pick the best browser-supported format
     *     via `iconFormatSuffix`, rewrite with `rewriteMediaURL`, then inject
     *     access params.
     *
     * @param {Node} node
     * @param {string} originalUrl - Raw icon URL from `node.design._iconOriginalUrl`
     * @param {number} scale - Current zoom scale (zoom.scale())
     * @returns {string}
     */
    function computeIconUrl(node, originalUrl, scale) {
        if (isExternalLink(originalUrl)) {
            return encodeURI(applyAccessParams(originalUrl));
        }
        const formatSuffix = KontextMap.iconFormatSuffix(originalUrl);
        if (formatSuffix === null) {
            // GIF, SVG, or unknown: rewrite domain/path prefix for CNAME
            // client domains (e.g. /client/{id}/{mapId}/… → /{mapSlug}/…)
            // but do not change variant or format.
            return encodeURI(
                applyAccessParams(
                    rewriteInternalUrlsForApp(originalUrl)
                )
            );
        }
        const dims = KontextMap.getNodeDimensions(node);
        const maxRendered = Math.max(dims.size.width, dims.size.height) * scale;
        const variant = KontextMap.iconVariantForRenderedSize(maxRendered);
        return encodeURI(
            applyAccessParams(
                rewriteMediaURL(originalUrl, {
                    useLegacy: true,
                    variant,
                    variantSuffix: formatSuffix
                })
            )
        );
    }

    /*
     * Create edges
     */
    const edges = model
        .selectAll('.edge')
        .data(filteredEdges, (d) => d.id)
        .enter()
        .append('div')
        .attr('class', (d) => ['edge', getZoomRangeClass(d)].join(' '));

    // Create lookup tables for nodes and edges
    const angleByEdgeId = {};
    const normalByEdgeId = {};

    edges.each(function (d) {
        const nodeFromId = d.node.from_id;
        const nodeToId = d.node.to_id;
        const nodeFrom = nodesById.get(String(nodeFromId));
        const nodeTo = nodesById.get(String(nodeToId));

        if (nodeFrom === undefined || nodeTo === undefined) {
            console.warn(
                'Missing point for edge %d: from=%d (%o), to=%d (%o)',
                d.id,
                nodeFromId,
                nodeFrom,
                nodeToId,
                nodeTo
            );
            return true;
        }

        if (
            d.visibility?.hidden ||
            nodeFrom.visibility?.hidden ||
            nodeTo.visibility?.hidden
        ) {
            return true;
        }

        const nodeFromDims = KontextMap.getNodeDimensions(nodeFrom);
        const nodeToDims = KontextMap.getNodeDimensions(nodeTo);
        const centerDx = nodeToDims.center.x - nodeFromDims.center.x;
        const centerDy = nodeToDims.center.y - nodeFromDims.center.y;
        const centerLen = Math.sqrt(centerDx * centerDx + centerDy * centerDy);
        const normal = {
            x: centerLen ? centerDx / centerLen : 0.0,
            y: centerLen ? centerDy / centerLen : 0.0,
            len: centerLen
        };
        const labelOffsetX =
            nodeFromDims.center.x <= nodeToDims.center.x
                ? { start: 0, middle: -50, end: -100 }
                : { start: -100, middle: -50, end: 0 };

        const edgeSegment = {
            start: nodeFromDims.center,
            end: nodeToDims.center
        };
        const intersectionPointFrom = KontextMap.intersectEdgeNode(
            edgeSegment,
            nodeFrom
        );
        const intersectionPointTo = KontextMap.intersectEdgeNode(
            edgeSegment,
            nodeTo
        );
        if (intersectionPointFrom === null) {
            console.warn(
                `No intersection between edge (${nodeFromId}, ${nodeToId}) and its starting node:`,
                edgeSegment,
                nodeFromDims
            );
            return true;
        }
        if (intersectionPointTo === null) {
            console.warn(
                `No intersection between edge (${nodeFromId}, ${nodeToId}) and its ending node:`,
                edgeSegment,
                nodeToDims
            );
            return true;
        }
        const edgeDx = intersectionPointTo.x - intersectionPointFrom.x;
        const edgeDy = intersectionPointTo.y - intersectionPointFrom.y;
        const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);

        const relLabelPos = {
            start: 0.0,
            middle: edgeLen / 2.0,
            end: edgeLen
        };

        const xMin = Math.min(nodeFromDims.center.x, nodeToDims.center.x);
        const yMin = Math.min(nodeFromDims.center.y, nodeToDims.center.y);
        const angle = centerDx
            ? (Math.atan(centerDy / centerDx) * 180.0) / Math.PI
            : y > 0.0
              ? 90.0
              : -90.0;

        const edge = d3.select(this);

        // Populate lookup tables
        normalByEdgeId[d.id] = normal;
        angleByEdgeId[d.id] = angle;

        const arrowStart =
            d.design.line_start && d.design.line_start !== 'none'
                ? d.design.line_start
                : null;
        const arrowStartSize = arrowStart
            ? (6 * d.design.line_start_size) / 100.0
            : 0;
        const arrowEnd =
            d.design.line_end && d.design.line_end !== 'none'
                ? d.design.line_end
                : null;
        const arrowEndSize = arrowEnd
            ? (6 * d.design.line_end_size) / 100.0
            : 0;
        const arrowPadding =
            arrowStart || arrowEnd
                ? Math.max(arrowStartSize, arrowEndSize) * d.design.border_width
                : d.design.border_width;

        edge.style(
            getVendorCSS('transform'),
            () =>
                `translate(${xMin - arrowPadding}px, ${yMin - arrowPadding}px)`
        ).style({
            width: `${Math.abs(centerDx) + 2 * arrowPadding}px`,
            height: `${Math.abs(centerDy) + 2 * arrowPadding}px`
        });
        const STROKE_ATTRS_BY_LINE_STYLE = {
            dashed: {
                dashArray: [3, 3],
                dashOffset: 1.5,
                lineCap: 'butt'
            },
            dotted: {
                dashArray: [0, 2],
                dashOffset: 0,
                lineCap: 'round'
            }
        };
        const strokeAttrs = STROKE_ATTRS_BY_LINE_STYLE[d.design.border_style];
        const dashArray = strokeAttrs?.dashArray.map(
            (value) => value * d.design.border_width
        );
        const edgeElem = edge.append('svg');

        const edgeDefs = edgeElem.append('svg:defs');

        switch (arrowEnd) {
            case 'dot':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowEnd}End${d.id}`,
                        viewBox: '-5 -5 10 10',
                        refX: 3,
                        refY: 0,
                        markerWidth: arrowEndSize,
                        markerHeight: arrowEndSize,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:circle')
                    .attr({
                        cx: 0,
                        cy: 0,
                        r: 5,
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
            case 'sharp':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowEnd}End${d.id}`,
                        viewBox: '0 -5 10 10',
                        refX: 8,
                        refY: 0,
                        markerWidth: arrowEndSize,
                        markerHeight: arrowEndSize,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:polygon')
                    .attr({
                        points: '1,-5 10,0 1,5 4,0',
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
            case 'straight':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowEnd}End${d.id}`,
                        viewBox: '0 -5 10 10',
                        refX: 8,
                        refY: 0,
                        markerWidth: arrowEndSize,
                        markerHeight: arrowEndSize,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:polygon')
                    .attr({
                        points: '0,-5 10,0 0,5',
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
            case 'wide':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowEnd}End${d.id}`,
                        viewBox: '0 -5 10 10',
                        refX: 8,
                        refY: 0,
                        markerWidth: arrowEndSize * 1.1,
                        markerHeight: arrowEndSize * 1.1,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:polygon')
                    .attr({
                        points: '5,-5 4,-4 8,0 4,4 5,5 10,0',
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
        }
        switch (arrowStart) {
            case 'dot':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowStart}Start${d.id}`,
                        viewBox: '-5 -5 10 10',
                        refX: -3,
                        refY: 0,
                        markerWidth: arrowStartSize,
                        markerHeight: arrowStartSize,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:circle')
                    .attr({
                        cx: 0,
                        cy: 0,
                        r: 5,
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
            case 'sharp':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowStart}Start${d.id}`,
                        viewBox: '0 -5 10 10',
                        refX: 2,
                        refY: 0,
                        markerWidth: arrowStartSize,
                        markerHeight: arrowStartSize,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:polygon')
                    .attr({
                        points: '9,-5 0,0 9,5 6,0',
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
            case 'straight':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowStart}Start${d.id}`,
                        viewBox: '0 -5 10 10',
                        refX: 2,
                        refY: 0,
                        markerWidth: arrowStartSize,
                        markerHeight: arrowStartSize,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:polygon')
                    .attr({
                        points: '0,0 10,-5 10,5',
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
            case 'wide':
                edgeDefs
                    .append('svg:marker')
                    .attr({
                        id: `${arrowStart}Start${d.id}`,
                        viewBox: '0 -5 10 10',
                        refX: 2,
                        refY: 0,
                        markerWidth: arrowStartSize * 1.1,
                        markerHeight: arrowStartSize * 1.1,
                        markerUnits: 'strokeWidth',
                        orient: 'auto'
                    })
                    .append('svg:polygon')
                    .attr({
                        points: '5,-5 6,-4 2,0 6,4 5,5 0,0',
                        fill: d.design.border_color,
                        stroke: 'none'
                    });
                break;
        }

        // Shrink the line to avoid overlap of line and arrow
        const arrowOffsetStart = arrowStart ? d.design.border_width : 0.0;
        const arrowOffsetEnd = arrowEnd ? d.design.border_width : 0.0;

        edgeElem.append('svg:line').attr({
            x1: numStr(
                intersectionPointFrom.x -
                    xMin +
                    normal.x * arrowOffsetStart +
                    arrowPadding
            ),
            y1: numStr(
                intersectionPointFrom.y -
                    yMin +
                    normal.y * arrowOffsetStart +
                    arrowPadding
            ),
            x2: numStr(
                intersectionPointTo.x -
                    xMin -
                    normal.x * arrowOffsetEnd +
                    arrowPadding
            ),
            y2: numStr(
                intersectionPointTo.y -
                    yMin -
                    normal.y * arrowOffsetEnd +
                    arrowPadding
            ),
            stroke: d.design.border_color,
            'stroke-dasharray': dashArray?.join(','),
            'stroke-dashoffset': strokeAttrs?.dashOffset,
            'stroke-linecap': strokeAttrs?.lineCap,
            'stroke-width': d.design.border_width,
            'marker-start': arrowStart
                ? `url(#${arrowStart}Start${d.id})`
                : null,
            'marker-end': arrowEnd ? `url(#${arrowEnd}End${d.id})` : null
        });
        const zoomRangeClass = getZoomRangeClass({
            visibility: {
                min_level: d.visibility.min_level || minZoomLevel,
                max_level: d.visibility.max_level || maxZoomLevel
            }
        });
        const textPosition = d.design.text_position || 'top';
        const relativeOffsetFromBottom = {
            top: 1.5,
            center: 0.8,
            bottom: -0.5
        }[textPosition];
        const labelOffsetY =
            d.design.border_width / 2 +
            relativeOffsetFromBottom * d.design.font_size;
        const labelCount =
            (translateStr(d.content.start, language, fallbackLanguage)
                ? 1
                : 0) +
            (translateStr(d.content.middle, language, fallbackLanguage)
                ? 1
                : 0) +
            (translateStr(d.content.end, language, fallbackLanguage) ? 1 : 0);
        ['start', 'middle', 'end'].forEach((l) => {
            const labelText = translateStr(
                d.content[l],
                language,
                fallbackLanguage
            );
            if (labelText) {
                edge.append('div')
                    .attr('class', `map-label label-${l} ${zoomRangeClass}`)
                    .style(
                        getVendorCSS('transform'),
                        `translate(${numStr(
                            intersectionPointFrom.x -
                                xMin +
                                normal.x * relLabelPos[l] +
                                arrowPadding
                        )}px, ${numStr(
                            intersectionPointFrom.y -
                                yMin +
                                normal.y * relLabelPos[l] +
                                arrowPadding
                        )}px)
                         rotate(${numStr(angle)}deg)
                         translate(${labelOffsetX[l]}%, ${-labelOffsetY}px)`
                    )
                    .style({
                        color: d.design.font_color,
                        'font-family': d.design._fontFamily,
                        'font-size': `${d.design.font_size}px`,
                        'font-style': (d) => {
                            const fontStyle = d.design.font_style;
                            return fontStyle !== 'normal' ? fontStyle : null;
                        },
                        'font-weight': (d) => {
                            const fontWeight = d.design.font_weight;
                            return fontWeight !== 400 ? fontWeight : null;
                        },
                        width:
                            labelCount < 2
                                ? `${numStr(edgeLen)}px`
                                : `${numStr(edgeLen / 3)}px`
                    })
                    .text(labelText);
            }
        });

        const showShadow = d.design.shadow_enabled;
        if (showShadow) {
            const shadowBlur = d.design.shadow_blur;
            const shadowColor = d.design.shadow_color;
            const shadowX = d.design.shadow_x;
            const shadowY = d.design.shadow_y;
            edge.style(
                'filter',
                `drop-shadow(${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowColor})`
            );
        }
    });

    /*
     * Create nodes
     */

    // Detect whether the map has a dark or light highlight backdrop so that
    // node colors can be adjusted for sufficient contrast when highlighted.
    const mapContainer = model.node()?.closest?.('.map2');
    const backdropDark = !!mapContainer?.classList.contains('backdrop--dark');
    const backdropLight = !!mapContainer?.classList.contains('backdrop--light');

    /**
     * Shifts an RGB color toward white (makeLight=true) or black (makeLight=false)
     * proportionally to its HSP luminance distance from the midpoint (127.5).
     * Returns null when no adjustment is needed (color is already on the
     * correct side of the midpoint).
     * @param {number} r
     * @param {number} g
     * @param {number} b
     * @param {boolean} makeLight
     * @returns {[number, number, number]|null}
     */
    function adjustColorForHighlight(r, g, b, makeLight) {
        const hsp = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b);
        const factor = makeLight
            ? Math.max(0, 1.0 - hsp / 127.5)
            : Math.max(0, hsp / 127.5 - 1.0);
        if (factor === 0) return null;
        if (makeLight) {
            return [
                Math.min(255, Math.round(r + (255 - r) * factor)),
                Math.min(255, Math.round(g + (255 - g) * factor)),
                Math.min(255, Math.round(b + (255 - b) * factor))
            ];
        }
        return [
            Math.max(0, Math.round(r * (1 - factor))),
            Math.max(0, Math.round(g * (1 - factor))),
            Math.max(0, Math.round(b * (1 - factor)))
        ];
    }

    const nodes = model
        .selectAll('.node')
        .data(filteredNodes, (d) => d.id)
        .enter()
        .append('div')
        .attr('class', (d) =>
            ['node', getNodeShapeClass(d), getZoomRangeClass(d)].join(' ')
        )
        .style(getVendorCSS('transform'), (d) => {
            const dims = KontextMap.getNodeDimensions(d);
            return `translate(${dims.center.x - dims.size.width_2}px, ${
                dims.center.y - dims.size.height_2
            }px)`;
        })
        .style('width', (d) => {
            const dims = KontextMap.getNodeDimensions(d);
            return `${dims.size.width}px`;
        })
        .style('height', (d) => {
            const dims = KontextMap.getNodeDimensions(d);
            return `${dims.size.height}px`;
        })
        .style({
            'border-radius': (d) =>
                `${d.design.shape === 'rectangle' ? 0 : 50}%`,
            'background-color': (d) => {
                const bgEnabled = d.design.bg_enabled;
                return bgEnabled === false ? 'transparent' : d.design.bg_color;
            },
            'border-color': (d) => d.design.border_color,
            'border-style': (d) => {
                const borderEnabled = d.design.border_enabled;
                return borderEnabled === false ? 'none' : d.design.border_style;
            },
            'border-width': (d) => `${d.design.border_width}px`,
            color: (d) => d.design.font_color,
            'font-family': (d) => d.design._fontFamily,
            'font-size': (d) => `${d.design.font_size}px`,
            'font-style': (d) => {
                const fontStyle = d.design.font_style;
                return fontStyle !== 'normal' ? fontStyle : null;
            },
            'font-weight': (d) => {
                const fontWeight = d.design.font_weight;
                return fontWeight !== 400 ? fontWeight : null;
            },
            'line-height': (d) => d.design.line_height
        });

    // Create node contents on demand to reduce number of DOM elements
    nodes.each(function (d) {
        const node = d3.select(this);
        const nodeDims = KontextMap.getNodeDimensions(d);

        if (d.visibility?.hidden) {
            return true;
        }

        // Create content wrapper
        const nodeContents = node
            .append('div')
            .attr('class', 'node-content-v2');

        const hasIcon = !!d.design._iconUrl;
        node.classed('has-icon', hasIcon);
        node.classed('has-no-icon', !hasIcon);

        const bgEnabledForClass = d.design.bg_enabled !== false;
        const bgColorForClass = bgEnabledForClass ? d.design.bg_color : null;
        const borderEnabledForClass = d.design.border_enabled !== false;
        const borderColorForClass =
            borderEnabledForClass && (d.design.border_width || 0) > 0
                ? d.design.border_color
                : null;
        node.classed(
            'node--dark-bg',
            !!bgColorForClass && isDarkColor(bgColorForClass)
        );
        node.classed(
            'node--dark-border',
            !!borderColorForClass && isDarkColor(borderColorForClass)
        );

        // Set per-node CSS variables for backdrop contrast adjustment.
        // Each variable is only written when the color actually needs shifting,
        // so the CSS [style*="--node-highlighted-*"] selectors fire only for
        // affected nodes (no-op for nodes that are already high-contrast).
        if (backdropDark || backdropLight) {
            const makeLight = backdropDark;

            if (bgEnabledForClass) {
                // Solid background: the bg and font form a designed contrast
                // pair — adjusting one without the other would break it.
                // Only the border is adjusted to make the node outline visible
                // against the backdrop; bg and font are left untouched.
                if (borderColorForClass) {
                    const [r, g, b] =
                        parseColor(borderColorForClass);
                    if (!Number.isNaN(r)) {
                        const adj = adjustColorForHighlight(r, g, b, makeLight);
                        if (adj) {
                            node.style(
                                '--node-highlighted-border',
                                `rgb(${adj[0]}, ${adj[1]}, ${adj[2]})`
                            );
                        }
                    }
                }
            } else {
                // Transparent background: border and centered label both appear
                // directly over the backdrop, so both are adjusted independently.
                if (borderColorForClass) {
                    const [r, g, b] =
                        parseColor(borderColorForClass);
                    if (!Number.isNaN(r)) {
                        const adj = adjustColorForHighlight(r, g, b, makeLight);
                        if (adj) {
                            node.style(
                                '--node-highlighted-border',
                                `rgb(${adj[0]}, ${adj[1]}, ${adj[2]})`
                            );
                        }
                    }
                }
                const fontColor = d.design.font_color;
                if (fontColor && !d.design._iconUrl) {
                    const [r, g, b] = parseColor(fontColor);
                    if (!Number.isNaN(r)) {
                        const adj = adjustColorForHighlight(r, g, b, makeLight);
                        if (adj) {
                            node.style(
                                '--node-highlighted-font',
                                `rgb(${adj[0]}, ${adj[1]}, ${adj[2]})`
                            );
                        }
                    }
                }
            }
        }

        if (hasIcon) {
            let iconURL;
            if (d.design._iconOriginalUrl) {
                iconURL = computeIconUrl(
                    d,
                    d.design._iconOriginalUrl,
                    zoom.scale()
                );
            } else {
                // External icons only: _iconOriginalUrl is always set for
                // internal icons, so this branch is never reached for them.
                iconURL = encodeURI(applyAccessParams(d.design._iconUrl));
            }

            let iconOpacity = (d.design.media_opacity || 100) / 100.0;
            iconOpacity = Math.max(0, iconOpacity);
            iconOpacity = Math.min(1, iconOpacity);

            const isAnimation = KontextMap.isAnimationUrl(
                d.design._iconOriginalUrl || d.design._iconUrl
            );

            const iconWrapper = nodeContents
                .append('div')
                .attr('class', 'icon-wrapper')
                .style({
                    'border-radius': `${
                        d.design.shape === 'rectangle' ? 0 : 50
                    }%`,
                    height: `${nodeDims.size.height}px`,
                    opacity: iconOpacity
                });

            if (isAnimation && window.lottie) {
                // Animation files (Lottie / dotLottie) are rendered via
                // lottie-web into an SVG element inside the icon container.
                const animationContainer = iconWrapper
                    .append('div')
                    .attr('class', 'custom-icon custom-icon--animation')
                    // Decorative: the point's own title names it.
                    .attr('aria-hidden', 'true')
                    .node();
                // An icon that loops forever with no way to stop it is
                // exactly what WCAG 2.2.2 is about. Loading with autoplay
                // off still renders the first frame, so the icon is there,
                // it just holds still.
                const animate = !a11y?.prefersReducedMotion();
                if (
                    KontextMap.isDotLottieUrl(iconURL) &&
                    window.loadDotLottieAnimation
                ) {
                    // Binary dotLottie container — extract animation data
                    // with inlined assets, then hand off to lottie-web.
                    window
                        .loadDotLottieAnimation(iconURL)
                        .then((animationData) => {
                            window.lottie.loadAnimation({
                                container: animationContainer,
                                renderer: 'svg',
                                loop: animate,
                                autoplay: animate,
                                animationData
                            });
                        });
                } else {
                    // Plain-JSON Lottie (.lot / .lottie.json) — lottie-web
                    // fetches and parses the JSON directly.
                    window.lottie.loadAnimation({
                        container: animationContainer,
                        renderer: 'svg',
                        loop: animate,
                        autoplay: animate,
                        path: iconURL
                    });
                }
            } else {
                iconWrapper.html(
                    `<div class="custom-icon" style="background-image:url(${iconURL})"/>`
                );
            }

            const iconCropped = d.design.media_cropped;
            if (iconCropped !== undefined) {
                iconWrapper.style(
                    'overflow',
                    iconCropped ? 'hidden' : 'visible'
                );
            }

            const iconSize = d.design.media_size;
            if (iconSize && iconSize !== 100) {
                iconWrapper
                    .select('.custom-icon')
                    .style(
                        getVendorCSS('transform'),
                        `scale(${iconSize / 100})`
                    );
            }
        }

        // Remove border styles from node (borders are on a dedicated element)
        node.style({
            'border-color': null,
            'border-style': null,
            'border-width': '0'
        });

        // Set CSS variables
        const borderEnabled = d.design.border_enabled;
        node.style({
            '--border-width': `${borderEnabled ? d.design.border_width : 0}px`
        });

        // Add border style to a dedicated element
        nodeContents
            .append('div')
            .attr('class', 'border-container')
            .style({
                'border-radius': (d) =>
                    `${d.design.shape === 'rectangle' ? 0 : 50}%`,
                'border-color': (d) => d.design.border_color,
                'border-style': (d) => {
                    return borderEnabled === false
                        ? 'none'
                        : d.design.border_style;
                },
                'border-width': 'var(--border-width)',
                width: 'calc(100% - 2 * var(--border-width))',
                height: 'calc(100% - 2 * var(--border-width))'
            });

        // Add CSS class depending on whether background is filled or transparent
        const bgEnabled = !!d.design.bg_enabled;
        node.classed('bg--filled', bgEnabled);
        node.classed('bg--transparent', !bgEnabled);

        let title = null;
        if (translateStr(d.content.title, language, fallbackLanguage)) {
            const posClass = KontextMap.titlePositionClass(
                d.design.title_position
            );
            title = nodeContents
                .append('span')
                .attr('class', `title ${posClass}`)
                .html(
                    translateStr(d.content.title, language, fallbackLanguage)
                );
        }

        let shortContent = null;
        if (translateStr(d.content.short_content, language, fallbackLanguage)) {
            shortContent = nodeContents
                .append('span')
                .attr('class', 'short-content')
                .html(
                    translateStr(
                        d.content.short_content,
                        language,
                        fallbackLanguage
                    )
                );
        }

        const textAlign = d.design.text_align;
        if (textAlign) {
            const justifyValue =
                {
                    left: 'flex-start',
                    right: 'flex-end'
                }[textAlign] || textAlign;
            const textAlignStyles = {
                '-webkit-box-pack': justifyValue,
                '-ms-flex-pack': justifyValue,
                'justify-content': justifyValue,
                'text-align': textAlign
            };
            title?.style(textAlignStyles);
            shortContent?.style(textAlignStyles);
        }

        const shadowEnabled = d.design.shadow_enabled;
        if (shadowEnabled) {
            const shadowBlur = d.design.shadow_blur;
            const shadowColor = d.design.shadow_color;
            const shadowX = d.design.shadow_x;
            const shadowY = d.design.shadow_y;
            node.style(
                'box-shadow',
                `${shadowX}px ${shadowY}px ${shadowBlur}px ${shadowColor}`
            );
        }
    });

    // When format detection completes, refresh all icon URLs to use the best
    // format (AVIF or WebP instead of the initial JPEG fallback).
    function refreshIconFormats() {
        const scale = zoom.scale();
        model.selectAll('.node').each(function (d) {
            const originalUrl = d.design?._iconOriginalUrl;
            if (!originalUrl) return;
            if (KontextMap.isAnimationUrl(originalUrl)) return;
            const customIcon = this.querySelector('.custom-icon');
            if (!customIcon) return;
            customIcon.dataset.iconVariant = '';
            customIcon.style.backgroundImage = `url(${computeIconUrl(d, originalUrl, scale)})`;
        });
    }
    if (KontextMap._formatDetectionDone) {
        if (KontextMap._detectedFormatSuffix !== '.jpg') {
            refreshIconFormats();
        }
    } else {
        KontextMap._formatDetectionCallbacks.push(() => {
            if (KontextMap._detectedFormatSuffix !== '.jpg') {
                refreshIconFormats();
            }
        });
    }

    model
        .append('div')
        .attr('class', ['highlight-backdrop', 'tx-exclude'].join(' '));

    let minLevel;
    let maxLevel;

    function resized() {
        viewportWidth = Number.parseInt(viewport.style('width'), 10);
        viewportHeight = Number.parseInt(viewport.style('height'), 10);

        const longEdgeRatio = Math.min(
            viewportWidth / modelWidth,
            viewportHeight / modelHeight
        );
        const shortEdgeRatio = Math.max(
            viewportWidth / modelWidth,
            viewportHeight / modelHeight
        );

        const isPortrait = viewportWidth < viewportHeight;
        const minZoom = isPortrait
            ? 0.8 * shortEdgeRatio + 0.2 * longEdgeRatio
            : longEdgeRatio;
        minLevel = Math.log(minZoom) / Math.log(zoom.zoomFactor());
        maxLevel = minLevel + maxZoomLevel * zoomStepsPerLevel - minZoomLevel;
        const minScale = zoom.zoomFactor() ** minLevel;
        zoom.scaleExtent([minScale, Number.POSITIVE_INFINITY]);

        // Restrict zoom out, but allow infinite zoom in
        const scale = zoom.scale();
        if (scale < minScale) {
            zoom.scale(minScale);
        }

        zoomed();
    }

    resized(false);
    d3.select(window).on('resize', resized);

    // Zoom out as much as possible on start
    mapObj.centerTo(modelWidth / 2.0, modelHeight / 2.0, 1, false);

    return mapObj;
};
